// Background-work provider — keeps in-flight async actions alive across
// navigations, tab switches, and full PWA restarts.
//
// Two concerns live here:
//
//   1. Tracked JOBS (e.g. recipe-parse) — the provider holds a list of
//      server-side job ids, polls them on a tick, and reconciles on
//      visibilitychange. Result/error are persisted to localStorage so
//      a user who kicks off a parse, closes the tab, and comes back can
//      still see their result (within TTL).
//
//   2. DRAFTS (form state the user has typed but not submitted) — small,
//      typed JSON blobs keyed by slot name. Survives navigation; expires
//      after a short TTL so stale data doesn't haunt future sessions.
//
// Lifecycle rules (chosen carefully; see chat with Mohan 2026-05-26):
//
//   * Server is the source of truth. On every mount or tab focus, the
//     provider polls each tracked job ONCE before resuming the cadence
//     loop — so a job that already finished while we were away resolves
//     immediately, no zombie pill.
//   * Per-entry TTL. Jobs 24h, drafts 1h. The visibilitychange sweep
//     drops anything past TTL.
//   * Per-user namespace: fittrack:bg:<userId>:... — flips cleanly on
//     login/logout (same class of cross-user bug we just paid off at the
//     DB layer; not repeating it client-side).
//   * Idle backoff: polling pauses while document.hidden. Resume + one
//     immediate reconcile on visibilitychange.
//   * Bounded count: max 10 in-flight jobs per user (oldest dropped).
//   * Multi-tab: each tab polls independently. Acceptable; the server
//     handles concurrent reads, and a future BroadcastChannel pass can
//     deduplicate if it becomes wasteful.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import {
  getJob,
  type JobStatus,
  type ResolvedIngredient,
  type ResolvedRecipe,
} from "./api"
import { useAuth } from "./auth"

// ---- types ----

export type BgJobStatus =
  | "pending"
  | "running"
  | "succeeded"
  | "failed"
  | "expired" // server 404 — job is gone

/** Per-job review-time edits. Lives on the BgJob so it gets persisted +
 * scoped + TTL'd alongside the parse result itself — no extra storage
 * keys to manage. Survives navigation; resets only when the user
 * dismisses the job (commit or discard). */
export type ReviewEdits = {
  /** Mirror of result.ingredients with user-tweaked quantities. We
   * store the full list (not a sparse diff) so a future schema change
   * to ResolvedIngredient surfaces as a soft mismatch we can detect. */
  ingredients?: ResolvedIngredient[]
  /** User-edited dish name, distinct from the auto-derived default. */
  nameOverride?: string
}

export type BgJob = {
  /** Server-side job id. */
  id: string
  /** Logical type — for now just "recipe.parse"; future types can multiplex through the same pipe. */
  kind: "recipe.parse"
  /** Short human label rendered in the pill (e.g. trimmed input echo). */
  label: string
  /** Page that initiated this job — pill click returns the user here. */
  originPath: string
  /** Epoch ms. Used for TTL eviction + sort order. */
  createdAt: number
  status: BgJobStatus
  /** Present when status === "succeeded". Shape depends on `kind`. */
  result?: ResolvedRecipe
  /** User edits made during review (per-ingredient grams, name override).
   * Optional — present once the user touches the review panel. */
  reviewEdits?: ReviewEdits
  /** Present when status === "failed". */
  error?: string
}

/** Schema version stamped on each persisted payload. Bump on any
 * breaking shape change to BgJob, ReviewEdits, or the persisted-draft
 * envelope; the storage reader drops mismatches rather than risking a
 * partial deserialization crash months later. */
const SCHEMA_VERSION = 1
type Persisted<T> = { v: number; payload: T }

// Job time-to-live: well above the hard parse timeout (90s), short enough
// that an abandoned tab from yesterday doesn't show stale pills today.
const JOB_TTL_MS = 24 * 60 * 60 * 1000
// Drafts hold half-typed forms — short TTL so we don't pre-fill stale text.
const DRAFT_TTL_MS = 60 * 60 * 1000
// Poll cadence matches the inline polling that used to live in the
// composer; survives a small jitter without becoming hot-polling.
const POLL_INTERVAL_MS = 1500
const MAX_JOBS = 10

const STORAGE_NS = "fittrack:bg"

function jobsKey(userId: string) {
  return `${STORAGE_NS}:${userId}:jobs`
}
function draftKey(userId: string, slot: string) {
  return `${STORAGE_NS}:${userId}:draft:${slot}`
}

// ---- storage helpers (silent-failing on private mode / quota) ----

/** Versioned read. Returns the payload only if the schema version
 * matches; older or shape-mismatched entries are dropped silently so
 * a future release can change the BgJob shape without crashing on
 * old persisted state. */
function readVersioned<T>(key: string): T | null {
  if (typeof window === "undefined") return null
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    const wrapped = JSON.parse(raw) as Persisted<T> | T
    // Back-compat: legacy unwrapped value also accepted as a one-off
    // grace path; treated as v=0 and discarded.
    if (
      typeof wrapped === "object" &&
      wrapped !== null &&
      "v" in (wrapped as object) &&
      "payload" in (wrapped as object)
    ) {
      const w = wrapped as Persisted<T>
      if (w.v !== SCHEMA_VERSION) return null
      return w.payload
    }
    return null
  } catch {
    return null
  }
}

function writeVersioned<T>(key: string, payload: T) {
  if (typeof window === "undefined") return
  try {
    const wrapped: Persisted<T> = { v: SCHEMA_VERSION, payload }
    window.localStorage.setItem(key, JSON.stringify(wrapped))
  } catch {
    // quota / private-mode — degrade silently. The in-memory state
    // remains correct for the current session.
  }
}

function removeKey(key: string) {
  if (typeof window === "undefined") return
  try {
    window.localStorage.removeItem(key)
  } catch {
    /* noop */
  }
}

/**
 * Wipe every fittrack:bg:* key NOT in the keep-user's namespace. Called on
 * auth changes — sign-out (no current user) clears everything; switching
 * accounts in one browser clears the previous user's tracked work so
 * nothing leaks across the boundary.
 */
function wipeForeignKeys(keepUserId: string | null) {
  if (typeof window === "undefined") return
  try {
    const keepPrefix = keepUserId
      ? `${STORAGE_NS}:${keepUserId}:`
      : null
    const toRemove: string[] = []
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i)
      if (k && k.startsWith(`${STORAGE_NS}:`) && (!keepPrefix || !k.startsWith(keepPrefix))) {
        toRemove.push(k)
      }
    }
    for (const k of toRemove) window.localStorage.removeItem(k)
  } catch {
    /* noop */
  }
}

// ---- provider ----

type Ctx = {
  jobs: BgJob[]
  /**
   * Begin tracking a server-side job. Idempotent: re-registering the same
   * id keeps the existing entry intact.
   */
  trackParseJob: (jobId: string, label: string, originPath: string) => void
  /** Remove a job from the tracked list (after the user has dealt with it). */
  dismissJob: (jobId: string) => void
  /** Patch the review-time edits on a job (per-ingredient grams,
   * name override). Persists alongside the job; survives navigation. */
  updateJobEdits: (jobId: string, edits: ReviewEdits) => void
  /** Return the single tracked job for an origin path, if any. */
  jobForOrigin: (originPath: string) => BgJob | undefined
}

const BgWorkContext = createContext<Ctx>({
  jobs: [],
  trackParseJob: () => {},
  dismissJob: () => {},
  updateJobEdits: () => {},
  jobForOrigin: () => undefined,
})

/**
 * Top-level switch — only mounts the inner provider when authed. That
 * way the inner component can hydrate from storage SYNCHRONOUSLY in
 * its useState initializer (userId is guaranteed non-null at render
 * time), eliminating the previous "idle for one frame, then flip to
 * parsing" hydration flicker. When the user is anon/loading we fall
 * back to the default context (empty jobs + no-op functions), so
 * useBackgroundJobs() calls on the login page are safe.
 */
export function BackgroundWorkProvider({ children }: { children: ReactNode }) {
  const auth = useAuth()
  if (auth.status !== "authed") return <>{children}</>
  return (
    <AuthedBgWorkProvider key={auth.me.user.id} userId={auth.me.user.id}>
      {children}
    </AuthedBgWorkProvider>
  )
}

function AuthedBgWorkProvider({
  userId,
  children,
}: {
  userId: string
  children: ReactNode
}) {
  // Synchronous hydration from storage — userId is known before this
  // component renders, so jobs already populates on the first frame.
  const [jobs, setJobs] = useState<BgJob[]>(() => {
    wipeForeignKeys(userId)
    const persisted = readVersioned<BgJob[]>(jobsKey(userId)) ?? []
    const now = Date.now()
    return persisted
      .filter((j) => now - j.createdAt < JOB_TTL_MS)
      .sort((a, b) => b.createdAt - a.createdAt)
      .slice(0, MAX_JOBS)
  })

  // Source-of-truth ref for the polling loop (so it doesn't restart on
  // every state change).
  const jobsRef = useRef<BgJob[]>(jobs)
  jobsRef.current = jobs

  // Persist jobs on every change.
  useEffect(() => {
    writeVersioned(jobsKey(userId), jobs)
  }, [jobs, userId])

  // ---- cross-tab sync ----
  //
  // The `storage` event fires in OTHER tabs whenever this tab writes to
  // localStorage (not the writing tab itself, by design). Listening lets
  // tab B see the in-flight pill that tab A just enqueued, without
  // forcing a manual refresh. Drop the event if it targets a foreign
  // key (different user, different namespace).
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (!e.key || e.key !== jobsKey(userId)) return
      if (!e.newValue) {
        setJobs([])
        return
      }
      try {
        const wrapped = JSON.parse(e.newValue) as Persisted<BgJob[]>
        if (wrapped?.v !== SCHEMA_VERSION) return
        const now = Date.now()
        const fresh = (wrapped.payload ?? [])
          .filter((j) => now - j.createdAt < JOB_TTL_MS)
          .sort((a, b) => b.createdAt - a.createdAt)
          .slice(0, MAX_JOBS)
        setJobs(fresh)
      } catch {
        /* corrupted payload from another tab; ignore */
      }
    }
    window.addEventListener("storage", onStorage)
    return () => window.removeEventListener("storage", onStorage)
  }, [userId])

  // ---- logout wipe ----
  // The provider unmounts naturally when auth flips to anon (the outer
  // gate short-circuits), but we also need to physically clear storage
  // so the next account on this device starts clean.
  useEffect(() => {
    return () => {
      // Component unmount: if this fires because the user signed out,
      // wipe everything. (If it fires because a different user signed in,
      // wipeForeignKeys on the next mount handles the cleanup; calling
      // it here too is redundant-safe.)
      wipeForeignKeys(null)
    }
  }, [])

  // ---- polling loop with per-job backoff ----
  //
  // Per-job failure counter lives in a ref (outside React state — we
  // don't want re-renders just because a request failed). On consecutive
  // errors, exponentially back off the next poll attempt for THAT job
  // (other jobs continue polling at base cadence). Resets on success.
  const failureMeta = useRef<
    Map<string, { failures: number; nextPollAt: number }>
  >(new Map())

  useEffect(() => {
    let cancelled = false
    let timer: number | undefined

    async function reconcile(j: BgJob): Promise<BgJob | null> {
      try {
        const r: JobStatus = await getJob(j.id)
        // Success — reset backoff for this job.
        failureMeta.current.delete(j.id)
        if (r.status === "succeeded" && r.result) {
          return { ...j, status: "succeeded", result: r.result }
        }
        if (r.status === "failed") {
          return {
            ...j,
            status: "failed",
            error: r.error || "Parse failed.",
          }
        }
        // pending / running — keep as-is but reflect server state if it
        // flipped (cosmetic).
        if (j.status !== r.status) {
          return { ...j, status: r.status }
        }
        return null
      } catch (e: any) {
        // 404 = job no longer exists server-side (reaped or never persisted).
        if (e?.response?.status === 404) {
          failureMeta.current.delete(j.id)
          return { ...j, status: "expired" }
        }
        // Transient — apply exponential backoff. base 1.5s × 2^failures,
        // capped at 30s. Keeps a flaky network from hammering the API.
        const prev = failureMeta.current.get(j.id) ?? {
          failures: 0,
          nextPollAt: 0,
        }
        const failures = Math.min(prev.failures + 1, 5)
        const delay = Math.min(
          POLL_INTERVAL_MS * 2 ** failures,
          30_000,
        )
        failureMeta.current.set(j.id, {
          failures,
          nextPollAt: Date.now() + delay,
        })
        return null
      }
    }

    async function pollAll() {
      if (cancelled) return
      if (typeof document !== "undefined" && document.hidden) return
      const current = jobsRef.current
      const now = Date.now()
      const active = current.filter((j) => {
        if (j.status !== "pending" && j.status !== "running") return false
        const meta = failureMeta.current.get(j.id)
        if (meta && meta.nextPollAt > now) return false
        return true
      })
      if (active.length === 0) return
      const updates = await Promise.all(active.map(reconcile))
      if (cancelled) return
      const patches = new Map<string, BgJob>()
      updates.forEach((u, i) => {
        if (u) patches.set(active[i].id, u)
      })
      if (patches.size === 0) return
      setJobs((prev) => prev.map((j) => patches.get(j.id) ?? j))
    }

    function schedule() {
      timer = window.setTimeout(async () => {
        await pollAll()
        if (!cancelled) schedule()
      }, POLL_INTERVAL_MS)
    }

    function onVisibility() {
      if (typeof document !== "undefined" && !document.hidden) {
        // Tab regained focus — clear any backoff timers and force an
        // immediate reconcile. A networking issue that caused backoff
        // while we were hidden may have resolved.
        failureMeta.current.clear()
        void pollAll()
      }
    }

    schedule()
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility)
    }

    return () => {
      cancelled = true
      if (timer != null) clearTimeout(timer)
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility)
      }
    }
  }, [])

  // ---- TTL sweep on focus ----
  useEffect(() => {
    function sweep() {
      const now = Date.now()
      setJobs((prev) =>
        prev.filter((j) => now - j.createdAt < JOB_TTL_MS),
      )
    }
    sweep()
    document.addEventListener("visibilitychange", sweep)
    return () => document.removeEventListener("visibilitychange", sweep)
  }, [])

  // ---- imperative API ----

  const trackParseJob = useCallback(
    (jobId: string, label: string, originPath: string) => {
      setJobs((prev) => {
        if (prev.some((j) => j.id === jobId)) return prev
        const next: BgJob = {
          id: jobId,
          kind: "recipe.parse",
          label,
          originPath,
          createdAt: Date.now(),
          status: "pending",
        }
        return [next, ...prev].slice(0, MAX_JOBS)
      })
    },
    [],
  )

  const dismissJob = useCallback((jobId: string) => {
    failureMeta.current.delete(jobId)
    setJobs((prev) => prev.filter((j) => j.id !== jobId))
  }, [])

  const updateJobEdits = useCallback(
    (jobId: string, edits: ReviewEdits) => {
      setJobs((prev) =>
        prev.map((j) =>
          j.id === jobId
            ? { ...j, reviewEdits: { ...j.reviewEdits, ...edits } }
            : j,
        ),
      )
    },
    [],
  )

  const jobForOrigin = useCallback(
    (originPath: string) => {
      return jobs
        .filter((j) => j.originPath === originPath)
        .sort((a, b) => b.createdAt - a.createdAt)[0]
    },
    [jobs],
  )

  const value = useMemo<Ctx>(
    () => ({
      jobs,
      trackParseJob,
      dismissJob,
      updateJobEdits,
      jobForOrigin,
    }),
    [jobs, trackParseJob, dismissJob, updateJobEdits, jobForOrigin],
  )

  return <BgWorkContext.Provider value={value}>{children}</BgWorkContext.Provider>
}

export function useBackgroundJobs() {
  return useContext(BgWorkContext)
}

// ---- draft hook ----
//
// Tiny typed draft persistence. The composer's textarea + meal-type + date
// flow through this so a navigation away doesn't lose input. Drafts are
// per-user; TTL evicts stale entries on read.
//
// API mirrors useState's [value, setter] for drop-in replacement at call
// sites. The setter debounces writes to storage (300ms) so a keystroke
// burst doesn't hammer localStorage.

/** Versioned draft payload — wraps the user's value with a timestamp
 * for TTL eviction. The whole thing then goes through Persisted<T>'s
 * schema-version envelope, so two layers of safety check. */
type DraftPayload<T> = { value: T; ts: number }

/**
 * useDebouncedEffect — fire `fn` `delay` ms after the last dep change.
 * On unmount, flush the latest pending invocation immediately so a
 * navigation-induced unmount doesn't drop the last ~300ms of edits.
 *
 * Why this and not a debounce in the consumer: review hosts call
 * updateJobEdits on every keystroke, which triggers a setJobs in the
 * provider, which triggers a localStorage.setItem in the persist
 * effect. Without debouncing that chain runs ~60 times/minute on a
 * 5KB job blob → noticeable input lag on low-end devices. Coalescing
 * the writes turns it into 2-3 writes per typing burst.
 */
export function useDebouncedEffect(
  fn: () => void,
  deps: React.DependencyList,
  delay: number,
) {
  const fnRef = useRef(fn)
  // Latch the latest fn so the unmount-flush below can read the most
  // recent closure (with the freshest captured values).
  fnRef.current = fn

  // Debounce: clear pending timer when deps change, schedule a new one.
  useEffect(() => {
    const handle = window.setTimeout(() => fnRef.current(), delay)
    return () => window.clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delay, ...deps])

  // Unmount-only flush. Empty deps → cleanup fires only when the
  // component is going away. Calls the LATEST fn via fnRef so edits
  // made between the last debounce-tick and unmount aren't lost.
  useEffect(() => {
    return () => fnRef.current()
  }, [])
}

export function useDraft<T>(slot: string, defaultValue: T, ttlMs = DRAFT_TTL_MS) {
  const auth = useAuth()
  const userId = auth.status === "authed" ? auth.me.user.id : null

  // Synchronous init from storage. Anon users get the default and never
  // touch storage (their writes are no-ops).
  const [value, setValueState] = useState<T>(() => {
    if (!userId) return defaultValue
    const p = readVersioned<DraftPayload<T>>(draftKey(userId, slot))
    if (!p) return defaultValue
    if (Date.now() - p.ts > ttlMs) {
      removeKey(draftKey(userId, slot))
      return defaultValue
    }
    return p.value
  })

  // Re-hydrate if userId arrives after mount (e.g. AuthProvider was still
  // loading). In the new "provider only mounts when authed" world this
  // path should rarely fire, but keep it for safety.
  useEffect(() => {
    if (!userId) return
    const p = readVersioned<DraftPayload<T>>(draftKey(userId, slot))
    if (!p) return
    if (Date.now() - p.ts > ttlMs) {
      removeKey(draftKey(userId, slot))
      return
    }
    setValueState(p.value)
  }, [userId, slot, ttlMs])

  // Debounced write — keystroke bursts collapse into a single
  // localStorage round-trip every 300ms.
  const writeTimer = useRef<number | undefined>(undefined)
  const setValue = useCallback(
    (v: T | ((prev: T) => T)) => {
      setValueState((prev) => {
        const next =
          typeof v === "function" ? (v as (p: T) => T)(prev) : v
        if (userId) {
          if (writeTimer.current != null) clearTimeout(writeTimer.current)
          writeTimer.current = window.setTimeout(() => {
            writeVersioned<DraftPayload<T>>(draftKey(userId, slot), {
              value: next,
              ts: Date.now(),
            })
          }, 300)
        }
        return next
      })
    },
    [userId, slot],
  )

  const clear = useCallback(() => {
    if (userId) removeKey(draftKey(userId, slot))
    setValueState(defaultValue)
  }, [userId, slot, defaultValue])

  return [value, setValue, clear] as const
}
